// @ts-check
// Editor E/OU (filtro avançado das views, doc 06 seção 11, e condições do público, contrato 09 de público, seção 9):
// linhas "Onde", "E"/"OU" (operador do grupo inteiro), regras (campo, operando e valor) e subgrupos de um nível. O
// estado vem de uma loja (`FilterTreeStore`): o rascunho da view (view-filter-store.js) ou o público (audience-store.js).
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { getFieldIcon } from '../../lib/field-metadata.js';
import { getDefaultOperand, getFilterDisplayValue, getSubFieldLabel, operandNeedsValue } from '../../lib/view-filters.js';
import { ONE_TO_MANY_OPERANDS } from '../../../../shared/filter/view-filter-tree.js';
import { isOneToManyAudienceRelation } from '../../lib/audience-field-catalog.js';
import { groupItems } from '../../lib/filter-tree-state.js';
import { Icon } from '../../ui/icon.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { LightIconButton } from '../../ui/button.js';
import { FieldSelectMenu, FilterEditor } from './filter-editor.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../lib/view-filters.js').ViewFilter} ViewFilter
 * @typedef {import('./view-state.js').ViewFilterGroup} ViewFilterGroup
 * @typedef {import('./view-filter-store.js').FilterTreeStore} FilterTreeStore
 * @typedef {import('../../lib/audience-field-catalog.js').FieldCatalog} FieldCatalog
 * @typedef {{ store: FilterTreeStore, fieldCatalog: FieldCatalog, visibleFieldIds: Set<string>, problemsByFilterId: Map<string, string>,
 *   readOnly: boolean, canAddRule: boolean }} EditorScope
 */

/**
 * Primeira coluna da linha: "Onde", o seletor E/OU (segunda linha) ou o operador em texto.
 * @param {{ scope: EditorScope, group: ViewFilterGroup, index: number }} props
 */
function LogicalOperatorCell({ scope, group, index }) {
  if (index === 0) return html`<span class="advanced-filter-logical">${t('parity.advancedFilter.where')}</span>`;
  if (index > 1 || scope.readOnly) return html`<span class="advanced-filter-logical">${t(`parity.advancedFilter.operators.${group.logicalOperator}`)}</span>`;
  return html`<select
    class="advanced-filter-logical-select"
    aria-label=${t('parity.advancedFilter.operator')}
    value=${group.logicalOperator}
    onChange=${(event) => scope.store.dispatch({ type: 'setGroupOperator', groupId: group.id, logicalOperator: event.currentTarget.value })}
  >
    <option value="AND">${t('parity.advancedFilter.operators.AND')}</option>
    <option value="OR">${t('parity.advancedFilter.operators.OR')}</option>
  </select>`;
}

/**
 * Rótulo da regra: o campo, ou "Relação / Campo do destino / Sub-campo" atrás de uma relação.
 * @param {FieldMetadata} field @param {FieldMetadata | undefined} targetField @param {string | null | undefined} subFieldName
 */
export function ruleFieldLabel(field, targetField, subFieldName) {
  const parts = [translateMetadataLabel(field.label)];
  if (targetField) parts.push(translateMetadataLabel(targetField.label));
  if (subFieldName) parts.push(getSubFieldLabel(subFieldName));
  return parts.join(' / ');
}

/**
 * Campo, operando e valor de uma regra.
 * @param {{ scope: EditorScope, filter: ViewFilter }} props
 */
export function FilterRuleControls({ scope, filter }) {
  const fieldRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const valueRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [openMenu, setOpenMenu] = useState(/** @type {'field' | 'value' | null} */ (null));
  const { fieldCatalog } = scope;
  const field = fieldCatalog.getField(filter.fieldMetadataId);
  const targetField = filter.targetFieldMetadataId ? fieldCatalog.getField(filter.targetFieldMetadataId) : undefined;
  const isOneToMany = isOneToManyAudienceRelation(field);
  // na regra atrás de uma relação, o valor é do campo de destino
  const valueField = targetField ?? field;
  const oneToManyOperands = isOneToMany ? (targetField ? ONE_TO_MANY_OPERANDS : ONE_TO_MANY_OPERANDS.filter((operand) => !operandNeedsValue(operand))) : null;
  const operandLabel = isOneToMany ? (/** @type {string} */ operand) => t(`audience.oneToMany.${operand}`) : (/** @type {string} */ operand) => t(`filters.operands.${operand}`);
  const displayValue = valueField && (!isOneToMany || targetField) ? getFilterDisplayValue(filter, valueField) : '';
  const title = field ? ruleFieldLabel(field, targetField, filter.subFieldName) : '';
  return html`
    <button ref=${fieldRef} type="button" class="advanced-filter-select" disabled=${scope.readOnly} onClick=${() => setOpenMenu(openMenu === 'field' ? null : 'field')}>
      ${field ? html`<${Icon} name=${getFieldIcon(field)} size=${14} />` : null}
      <span>${field ? title : t('parity.advancedFilter.chooseField')}</span>
      <${Icon} name="IconChevronDown" size=${14} />
    </button>
    <${Dropdown} anchorRef=${fieldRef} isOpen=${openMenu === 'field'} onClose=${() => setOpenMenu(null)} width=${280}>
      <${FieldSelectMenu}
        title=${t('viewBar.filter')}
        fields=${fieldCatalog.fields}
        visibleFieldIds=${scope.visibleFieldIds}
        getSubFields=${fieldCatalog.getSubFields}
        getRelationTargets=${fieldCatalog.getRelationTargets}
        getTargetSubFields=${fieldCatalog.getTargetSubFields}
        onClose=${() => setOpenMenu(null)}
        onSelect=${(selected, subFieldName, selectedTarget) => {
          setOpenMenu(null);
          const operandField = selectedTarget ?? selected;
          const operand = isOneToManyAudienceRelation(selected)
            ? (selectedTarget ? 'IS' : 'IS_NOT_EMPTY')
            : getDefaultOperand(operandField, subFieldName ?? null);
          scope.store.dispatch({ type: 'updateFilter', filterId: filter.id, changes: {
            fieldMetadataId: selected.id, subFieldName: subFieldName ?? null, operand, value: '', displayValue: '',
            ...(fieldCatalog.getRelationTargets ? { targetFieldMetadataId: selectedTarget?.id ?? null } : {}),
          } });
        }}
      />
    <//>
    <button ref=${valueRef} type="button" class="advanced-filter-select advanced-filter-value" disabled=${!field || scope.readOnly}
      onClick=${() => setOpenMenu(openMenu === 'value' ? null : 'value')}>
      <span>${operandLabel(filter.operand)}${operandNeedsValue(filter.operand) ? html`<span class="advanced-filter-value-text">${displayValue ? `: ${displayValue}` : `: ${t('parity.advancedFilter.enterValue')}`}</span>` : null}</span>
      <${Icon} name="IconChevronDown" size=${14} />
    </button>
    ${field && valueField
      ? html`<${Dropdown} anchorRef=${valueRef} isOpen=${openMenu === 'value'} onClose=${() => setOpenMenu(null)} width=${valueField.type === 'RELATION' ? 280 : 240}>
          <${FilterEditor}
            filter=${filter}
            field=${valueField}
            title=${targetField || isOneToMany ? title : undefined}
            operands=${oneToManyOperands ?? undefined}
            operandLabel=${operandLabel}
            onChange=${(changes) => scope.store.dispatch({ type: 'updateFilter', filterId: filter.id, changes })}
            onClose=${() => setOpenMenu(null)}
          />
        <//>`
      : null}
  `;
}

/**
 * @param {{ scope: EditorScope, objectMetadata: ObjectMetadata, group: ViewFilterGroup, isRoot: boolean }} props
 */
function FilterGroupEditor({ scope, objectMetadata, group, isRoot }) {
  const addRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isAddOpen, setIsAddOpen] = useState(false);
  const { store, fieldCatalog } = scope;
  const firstField = fieldCatalog.fields[0];
  const items = groupItems({ filters: store.filters.value, filterGroups: store.filterGroups.value }, group.id);
  const addRule = () => {
    setIsAddOpen(false);
    if (firstField) store.dispatch({ type: 'addRule', groupId: group.id, fieldMetadataId: firstField.id, operand: getDefaultOperand(firstField) });
  };
  return html`<div class="advanced-filter-group" data-root=${isRoot ? 'true' : undefined}>
    ${items.map((item, index) => {
      const problem = item.kind === 'rule' ? scope.problemsByFilterId.get(item.filter.id) : undefined;
      return html`<div class="advanced-filter-row" key=${item.kind === 'rule' ? item.filter.id : item.group.id} data-problem=${problem ? 'true' : undefined}>
        <div class="advanced-filter-logical-cell"><${LogicalOperatorCell} scope=${scope} group=${group} index=${index} /></div>
        ${item.kind === 'rule'
          ? html`<${FilterRuleControls} scope=${scope} filter=${item.filter} />
              ${scope.readOnly ? null : html`<${LightIconButton} icon="IconX" ariaLabel=${t('parity.advancedFilter.removeRule')} onClick=${() => store.dispatch({ type: 'removeRule', filterId: item.filter.id })} />`}
              ${problem ? html`<div class="filter-rule-problem" role="note">${t(`audience.problems.${problem}`)}</div>` : null}`
          : html`<div class="advanced-filter-subgroup">
              <${FilterGroupEditor} scope=${scope} objectMetadata=${objectMetadata} group=${item.group} isRoot=${false} />
            </div>
            ${scope.readOnly ? null : html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('parity.advancedFilter.removeGroup')} onClick=${() => store.dispatch({ type: 'removeGroup', groupId: item.group.id })} />`}`}
      </div>`;
    })}
    ${scope.readOnly ? null : html`<div class="advanced-filter-actions">
      ${isRoot
        ? html`<button ref=${addRef} type="button" class="light-button" disabled=${!scope.canAddRule} onClick=${() => setIsAddOpen(!isAddOpen)}>
            <${Icon} name="IconPlus" size=${14} /><span>${t('parity.advancedFilter.addFilterRule')}</span>
          </button>
          <${Dropdown} anchorRef=${addRef} isOpen=${isAddOpen} onClose=${() => setIsAddOpen(false)} width=${200}>
            <${DropdownMenuItems}>
              <${MenuItem} icon="IconPlus" text=${t('parity.advancedFilter.addRule')} onClick=${addRule} />
              <${MenuItem} icon="IconLibraryPlus" text=${t('parity.advancedFilter.addGroup')} onClick=${() => {
                setIsAddOpen(false);
                if (firstField) store.dispatch({ type: 'addGroup', groupId: group.id, fieldMetadataId: firstField.id, operand: getDefaultOperand(firstField) });
              }} />
            <//>
          <//>`
        : html`<button type="button" class="light-button" disabled=${!scope.canAddRule} onClick=${addRule}>
            <${Icon} name="IconPlus" size=${14} /><span>${t('parity.advancedFilter.addRule')}</span>
          </button>`}
    </div>`}
  </div>`;
}

/**
 * Editor E/OU do grupo raiz da loja (nada quando ainda não há raiz).
 * @param {{ objectMetadata: ObjectMetadata, store: FilterTreeStore, fieldCatalog: FieldCatalog, visibleFieldIds?: Set<string>,
 *   maximumRules?: number, problemsByFilterId?: Map<string, string>, readOnly?: boolean }} props
 */
export function FilterTreeEditor({ objectMetadata, store, fieldCatalog, visibleFieldIds = new Set(), maximumRules = Infinity, problemsByFilterId = new Map(), readOnly = false }) {
  const root = store.filterGroups.value.find((group) => !group.parentViewFilterGroupId);
  if (!root) return null;
  /** @type {EditorScope} */
  const scope = { store, fieldCatalog, visibleFieldIds, problemsByFilterId, readOnly, canAddRule: store.filters.value.length < maximumRules };
  return html`<${FilterGroupEditor} scope=${scope} objectMetadata=${objectMetadata} group=${root} isRoot=${true} />`;
}
